import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import SiteHeader from '../components/SiteHeader';
import { useAuthed } from '../hooks/useAuthed';
import { useWorkoutSession } from '../hooks/useWorkoutSession';
import { gymApi } from '../api/gymApi';
import { PRE_WORKOUT_DRILLS, loadCompleted } from '../preWorkout';
import { clearWorkoutTimer, endWorkoutTimer, startWorkoutTimer } from '../workoutTimer';
import { useEffect } from 'react';

/**
 * Workout overview: the day's exercise list with progress, and the entry point
 * into each exercise. Tapping an exercise opens its own page (no modals).
 */
export default function WorkoutPage() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const authed = useAuthed();
  const { session, loading, error, saveError } = useWorkoutSession(sessionId);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState('');

  // Adopt the clock from the session itself, so a refresh mid-workout resumes
  // at the true start time rather than restarting from zero.
  useEffect(() => {
    if (!session) return;
    if (session.status === 'in_progress') startWorkoutTimer(session.sessionId, session.startedAt * 1000);
    else endWorkoutTimer(session.sessionId);
  }, [session]);

  if (loading) return <><SiteHeader /><main className="page page--narrow"><p className="muted">Loading…</p></main></>;
  if (error) return <><SiteHeader /><main className="page page--narrow"><p className="error">{error}</p></main></>;
  if (!session) return <><SiteHeader /><main className="page page--narrow"><p className="muted">Workout not found.</p></main></>;

  // Warm-up progress is client-side only; it never reaches the API.
  // (localStorage, so it survives a refresh or browser restart.)
  const warmupDoneList = sessionId ? loadCompleted(sessionId) : [];
  const warmupCount = warmupDoneList.length;
  const warmupDone = PRE_WORKOUT_DRILLS.every((d, i) => warmupDoneList.includes(i) || d.optional);

  const done = session.exercises.filter(e => e.completed || e.skipped).length;
  const total = session.exercises.length;
  const allDone = done === total;
  const finished = session.status !== 'in_progress';

  async function finish() {
    if (!sessionId) return;
    setBusy(true);
    setActionError('');
    try {
      await gymApi.completeWorkout(sessionId, authed);
      endWorkoutTimer(sessionId);
      navigate('/', { replace: true });
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Could not finish the workout');
      setBusy(false);
    }
  }

  async function abandon() {
    if (!sessionId) return;
    setBusy(true);
    setActionError('');
    try {
      await gymApi.abandonWorkout(sessionId, authed);
      // A discarded workout leaves no duration worth showing.
      clearWorkoutTimer();
      navigate('/', { replace: true });
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Could not discard the workout');
      setBusy(false);
    }
  }

  return (
    <>
    <SiteHeader back={{ label: 'Home', to: '/' }} />
    <main className="page page--narrow workout-page">
      <h1>{session.dayLabel}</h1>
      <p className="muted">{done} of {total} exercises done</p>
      <div className="progress-bar" aria-hidden="true">
        <div className="progress-bar-fill" style={{ width: `${(done / Math.max(1, total)) * 100}%` }} />
      </div>

      {saveError && <p className="warn" style={{ marginTop: 12 }}>{saveError} — your last changes may not be stored.</p>}
      {actionError && <p className="error">{actionError}</p>}

      <ul className="plain-list" style={{ marginTop: 16 }}>
        {/* The general warm-up leads the day. It is marked "W" rather than a
            number because it is not one of the program's prescribed exercises. */}
        <li>
          <button
            type="button"
            className={`exercise-row${warmupDone ? ' exercise-row--done' : ''}`}
            onClick={() => navigate(`/workout/${session.sessionId}/warmup`)}
            disabled={finished}
          >
            <span className="exercise-row-mark" aria-hidden="true">{warmupDone ? '✓' : 'W'}</span>
            <span className="exercise-row-body">
              <span className="exercise-row-name">General warm-up</span>
              <span className="muted">
                {warmupCount}/{PRE_WORKOUT_DRILLS.length} drills · cardio + mobility
              </span>
            </span>
            <span className="exercise-row-chevron" aria-hidden="true">›</span>
          </button>
        </li>

        {session.exercises.map(ex => {
          const setsDone = ex.sets.filter(s => s.completed && !s.warmup).length;
          const setsTotal = ex.sets.filter(s => !s.warmup).length;
          const state = ex.skipped ? 'skipped' : ex.completed ? 'done' : setsDone > 0 ? 'active' : 'todo';
          return (
            <li key={ex.index}>
              <button
                type="button"
                className={`exercise-row exercise-row--${state}`}
                onClick={() => navigate(`/workout/${session.sessionId}/${ex.index}`)}
                disabled={finished}
              >
                <span className="exercise-row-mark" aria-hidden="true">
                  {state === 'done' ? '✓' : state === 'skipped' ? '–' : ex.index}
                </span>
                <span className="exercise-row-body">
                  <span className="exercise-row-name">{ex.exerciseName}</span>
                  <span className="muted">
                    {setsDone}/{setsTotal} sets
                    {ex.targetReps ? ` · ${ex.targetReps} reps` : ''}
                  </span>
                </span>
                <span className="exercise-row-chevron" aria-hidden="true">›</span>
              </button>
            </li>
          );
        })}
      </ul>

      {!finished && (
        <>
          <button
            type="button"
            className="btn-primary btn-block btn-lg"
            style={{ marginTop: 24 }}
            onClick={finish}
            disabled={busy}
          >
            {busy ? 'Finishing…' : allDone ? 'Finish workout' : `Finish workout (${total - done} left)`}
          </button>
          <button
            type="button"
            className="btn-quiet btn-block"
            style={{ marginTop: 10 }}
            onClick={abandon}
            disabled={busy}
          >
            Discard this workout
          </button>
        </>
      )}
    </main>
    </>
  );
}
